import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { 
  Flag, ChevronLeft, ChevronRight, Clock, CheckCircle, 
  AlertTriangle, XCircle, ArrowUpRight
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/admin`;

const AdminReports = () => {
  const navigate = useNavigate();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    fetchReports();
  }, [page, statusFilter]);

  const fetchReports = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '20'
      });
      
      if (statusFilter !== 'all') params.append('status', statusFilter);

      const response = await axios.get(`${API}/reports?${params.toString()}`);
      setReports(response.data.reports);
      setTotalPages(response.data.pages);
      setTotal(response.data.total);
    } catch (error) {
      toast.error('Failed to fetch reports');
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending': return <Clock className="w-4 h-4 text-yellow-500" />;
      case 'in_progress': return <AlertTriangle className="w-4 h-4 text-blue-500" />;
      case 'resolved': return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'escalated': return <ArrowUpRight className="w-4 h-4 text-red-500" />;
      case 'dismissed': return <XCircle className="w-4 h-4 text-gray-500" />;
      default: return <Flag className="w-4 h-4" />;
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-500/10 text-yellow-500',
      in_progress: 'bg-blue-500/10 text-blue-500',
      resolved: 'bg-green-500/10 text-green-500',
      escalated: 'bg-red-500/10 text-red-500',
      dismissed: 'bg-gray-500/10 text-gray-500'
    };
    return (
      <span className={`px-2 py-1 rounded text-xs font-medium flex items-center gap-1 ${styles[status] || ''}`}>
        {getStatusIcon(status)}
        {status.replace('_', ' ')}
      </span>
    );
  };

  const getPhotoUrl = (user) => {
    if (user?.public_photos?.[0]) {
      const photo = user.public_photos[0];
      if (photo.startsWith('data:') || photo.startsWith('http')) return photo;
      return `data:image/jpeg;base64,${photo}`;
    }
    return 'https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=100';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Reports</h1>
          <p className="text-[#A1A1AA]">{total} total reports</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-4">
        <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
          <SelectTrigger className="w-48 bg-[#111] border-white/10">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent className="bg-[#111] border-white/10">
            <SelectItem value="all">All Reports</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="in_progress">In Progress</SelectItem>
            <SelectItem value="resolved">Resolved</SelectItem>
            <SelectItem value="escalated">Escalated</SelectItem>
            <SelectItem value="dismissed">Dismissed</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Reports Table */}
      <div className="bg-[#111] border border-white/10 rounded-xl overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="w-8 h-8 border-2 border-[#FF2E63] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : reports.length === 0 ? (
          <div className="text-center py-12">
            <Flag className="w-12 h-12 text-[#52525B] mx-auto mb-3" />
            <p className="text-[#A1A1AA]">No reports found</p>
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-white/5">
              <tr>
                <th className="text-left p-4 text-xs font-medium text-[#A1A1AA] uppercase">Reported User</th>
                <th className="text-left p-4 text-xs font-medium text-[#A1A1AA] uppercase">Reporter</th>
                <th className="text-left p-4 text-xs font-medium text-[#A1A1AA] uppercase">Reason</th>
                <th className="text-left p-4 text-xs font-medium text-[#A1A1AA] uppercase">Status</th>
                <th className="text-left p-4 text-xs font-medium text-[#A1A1AA] uppercase">Date</th>
                <th className="text-right p-4 text-xs font-medium text-[#A1A1AA] uppercase">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {reports.map((report) => (
                <tr key={report.id} className="hover:bg-white/5">
                  <td className="p-4">
                    <div className="flex items-center gap-3">
                      <img
                        src={getPhotoUrl(report.reported_user)}
                        alt=""
                        className="w-10 h-10 rounded-full object-cover"
                      />
                      <div>
                        <p className="text-white font-medium">
                          {report.reported_user?.display_name || report.reported_user?.username || 'Unknown'}
                        </p>
                        <p className="text-sm text-[#52525B]">@{report.reported_user?.username}</p>
                      </div>
                    </div>
                  </td>
                  <td className="p-4">
                    <p className="text-[#A1A1AA]">
                      {report.reporter?.display_name || report.reporter?.username || 'Unknown'}
                    </p>
                  </td>
                  <td className="p-4">
                    <p className="text-[#A1A1AA] truncate max-w-xs">{report.reason}</p>
                  </td>
                  <td className="p-4">
                    {getStatusBadge(report.status)}
                  </td>
                  <td className="p-4 text-[#52525B] text-sm">
                    {new Date(report.timestamp).toLocaleDateString()}
                  </td>
                  <td className="p-4 text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => navigate(`/admin/reports/${report.id}`)}
                      className="text-[#FF2E63]"
                    >
                      Review
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between p-4 border-t border-white/10">
            <p className="text-sm text-[#A1A1AA]">
              Page {page} of {totalPages}
            </p>
            <div className="flex gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="text-[#A1A1AA]"
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Previous
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="text-[#A1A1AA]"
              >
                Next
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminReports;
